import React, { useMemo } from 'react';
import { Icon } from './StockControl';

const formatDate = (value) => {
  if (!value) return 'Sin fecha';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
};

const getVirtualAssistantTimestampMs = (entry = {}) => {
  const sources = [entry.appliedAt, entry.createdAt, entry.updatedAt, entry.endTime, entry.startTime];
  for (const source of sources) {
    if (!source) continue;
    if (typeof source?.toDate === 'function') {
      try {
        return source.toDate().getTime();
      } catch {
        continue;
      }
    }
    const parsed = new Date(source);
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
  }
  return 0;
};

const StatCard = ({ title, value, subtitle, tone = 'cyan' }) => {
  const tones = {
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    violet: 'border-violet-200 bg-violet-50 text-violet-900',
  };

  return (
    <div className={`rounded-3xl border p-4 ${tones[tone] || tones.cyan}`}>
      <p className="text-[11px] font-black uppercase tracking-[0.16em] opacity-70">{title}</p>
      <p className="mt-3 text-3xl font-black">{value}</p>
      <p className="mt-2 text-sm opacity-80">{subtitle}</p>
    </div>
  );
};

const SectionCard = ({ iconPath, iconTone = 'text-slate-700 bg-slate-100', title, children }) => (
  <div className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`rounded-2xl p-3 ${iconTone}`}>
        <Icon path={iconPath} className="w-5 h-5" />
      </div>
      <h3 className="text-lg font-black text-stone-900">{title}</h3>
    </div>
    <div className="mt-4 text-sm text-stone-600">{children}</div>
  </div>
);

const QuickActionButton = ({ label, tone = 'slate', onClick }) => {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:text-slate-950',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-800 hover:border-cyan-300',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-300',
    violet: 'border-violet-200 bg-violet-50 text-violet-800 hover:border-violet-300',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-semibold transition-all duration-200 ${tones[tone] || tones.slate}`}
    >
      {label}
    </button>
  );
};

const VirtualAssistant = ({
  tasks = [],
  allHistory = [],
  products = [],
  purchases = [],
  outboundOrders = [],
  movements = [],
  onStartTask,
  onGoToPanel,
}) => {
  const metrics = useMemo(() => {
    const pendingTasks = tasks.filter((task) => task.status === 'pending');
    const completedTasks = tasks.filter((task) => task.status === 'completed');
    const nextPendingTask = [...pendingTasks].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')))[0] || null;
    const latestControls = [...allHistory]
      .sort((a, b) => {
        const aDate = typeof a?.endTime?.toDate === 'function' ? a.endTime.toDate().getTime() : 0;
        const bDate = typeof b?.endTime?.toDate === 'function' ? b.endTime.toDate().getTime() : 0;
        return bDate - aDate;
      })
      .slice(0, 3);

    const controlsWithDifferences = allHistory.filter((item) => {
      try {
        const details = JSON.parse(item.countDetails || '[]');
        return details.some((detail) => Number(detail?.difference || 0) !== 0);
      } catch {
        return false;
      }
    }).length;

    const liveProducts = products.filter((product) => {
      const buckets = product?.stockBuckets || {};
      return Number(buckets.physicalQty || 0) > 0
        || Number(buckets.pendingInboundQty || 0) > 0
        || Number(buckets.inboundTransitQty || 0) > 0
        || Number(buckets.pendingOutboundQty || 0) > 0
        || Number(buckets.outboundTransitQty || 0) > 0;
    }).length;

    const readyPurchases = purchases.filter((purchase) => ['pending_pickup', 'assigned_to_route', 'in_transit'].includes(String(purchase.status || '').trim())).length;
    const readyOutboundOrders = outboundOrders.filter((order) => ['ready_for_delivery', 'partially_assigned', 'assigned_to_route', 'in_transit', 'partially_delivered'].includes(String(order.status || '').trim())).length;
    const recentMovementCount = movements.filter((movement) => getVirtualAssistantTimestampMs(movement) > 0).length;

    let suggestedPanel = 'planner';
    if (readyPurchases > 0) suggestedPanel = 'purchases';
    else if (readyOutboundOrders > 0) suggestedPanel = 'outbound-orders';
    else if (recentMovementCount > 0) suggestedPanel = 'movements';
    else if (liveProducts > 0) suggestedPanel = 'products';

    return {
      pendingTasks,
      completedTasks,
      nextPendingTask,
      latestControls,
      controlsWithDifferences,
      liveProducts,
      readyPurchases,
      readyOutboundOrders,
      recentMovementCount,
      suggestedPanel,
    };
  }, [allHistory, movements, outboundOrders, products, purchases, tasks]);

  const quickChecklist = [
    'Empezá por Compras / pendientes si hay retiros vivos o mercadería por ingresar.',
    'Usá Pedidos de salida para preparar entregas antes de pasar por Logística.',
    'Leé Movimientos cuando quieras ver costo, venta, margen y traza en una misma planilla.',
    'Apoyate en Controles físicos y Estadísticas para validar sistema vs depósito sin perder historial.',
  ];

  return (
    <div className="h-full overflow-y-auto rounded-3xl border border-stone-200 bg-gradient-to-br from-white via-stone-50 to-blue-50 p-5 shadow-xl">
      <div className="rounded-[1.75rem] bg-slate-950 px-5 py-5 text-white shadow-lg">
        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-200">
          <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="w-4 h-4" />
          GIA Stock
        </div>
        <h2 className="mt-4 text-3xl font-black tracking-tight">Guía operativa del módulo</h2>
        <p className="mt-3 max-w-3xl text-sm text-slate-300">
          Este módulo organiza productos, ingresos, pedidos de salida, movimientos y control físico en una sola lectura.
          Según el nivel de tu plan, después puede profundizar trazabilidad, seguimiento y análisis sobre cada ciclo.
        </p>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Productos vivos" value={metrics.liveProducts} subtitle="Referencias con stock o movimiento operativo actual." tone="cyan" />
        <StatCard title="Ingresos en curso" value={metrics.readyPurchases} subtitle="Compras todavía conectadas con retiro, tránsito o validación." tone="amber" />
        <StatCard title="Salidas vivas" value={metrics.readyOutboundOrders} subtitle="Pedidos preparados o todavía conectados con Logística." tone="emerald" />
        <StatCard title="Movimientos" value={metrics.recentMovementCount} subtitle="Eventos reales disponibles para leer traza, costo y margen." tone="violet" />
      </div>

      <div className="mt-6 grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
        <SectionCard iconPath="M9 12.75 11.25 15 15 9.75" iconTone="bg-cyan-100 text-cyan-700" title="Próximo paso sugerido">
          {metrics.nextPendingTask ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-cyan-700">Próximo control</p>
                <p className="mt-2 text-xl font-black text-stone-900">{metrics.nextPendingTask.productName}</p>
                <p className="mt-2 text-sm text-stone-600">Fecha prevista: {formatDate(metrics.nextPendingTask.date)}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => onStartTask?.(metrics.nextPendingTask.productId, metrics.nextPendingTask)}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
                >
                  <Icon path="M5.25 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.574 0 3.286l-11.54 6.347c-1.25.687-2.779-.216-2.779-1.643V5.653z" className="w-5 h-5" />
                  Iniciar próximo control
                </button>
                <QuickActionButton
                  label="Abrir frente sugerido"
                  tone="cyan"
                  onClick={() => onGoToPanel?.(metrics.suggestedPanel)}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p>No hay controles inmediatos. Este es un buen momento para revisar el frente que hoy concentra más valor operativo.</p>
              <QuickActionButton
                label={metrics.suggestedPanel === 'purchases' ? 'Ir a Compras / pendientes'
                  : metrics.suggestedPanel === 'outbound-orders' ? 'Ir a Pedidos de salida'
                  : metrics.suggestedPanel === 'movements' ? 'Ir a Movimientos'
                  : 'Ir a Productos'}
                tone="cyan"
                onClick={() => onGoToPanel?.(metrics.suggestedPanel)}
              />
            </div>
          )}
        </SectionCard>

        <SectionCard iconPath="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" iconTone="bg-violet-100 text-violet-700" title="Checklist de uso">
          <ul className="space-y-3">
            {quickChecklist.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <Icon path="M4.5 12.75l6 6 9-13.5" className="mt-0.5 w-5 h-5 text-emerald-500" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <div className="mt-6 grid gap-5 xl:grid-cols-2">
        <SectionCard iconPath="M4.5 12.75l6 6 9-13.5" iconTone="bg-emerald-100 text-emerald-700" title="Lectura rápida del historial">
          {metrics.latestControls.length > 0 ? (
            <div className="space-y-3">
              {metrics.latestControls.map((item) => (
                <div key={item.id || `${item.productName}-${item.endTime?.seconds || item.endTime || 'control'}`} className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                  <p className="font-black text-stone-900">{item.productName}</p>
                  <p className="mt-1 text-sm text-stone-600">Cierre: {typeof item?.endTime?.toDate === 'function' ? formatDate(item.endTime.toDate()) : 'reciente'}</p>
                </div>
              ))}
            </div>
          ) : (
            <p>Todavía no hay historial suficiente para mostrar una secuencia reciente dentro de este módulo.</p>
          )}
        </SectionCard>

        <SectionCard iconPath="M8.25 6.75h7.5M8.25 10.5h7.5M8.25 14.25H12m-7.5 6h15a2.25 2.25 0 002.25-2.25V6A2.25 2.25 0 0019.5 3.75h-15A2.25 2.25 0 002.25 6v12A2.25 2.25 0 004.5 20.25z" iconTone="bg-slate-100 text-slate-700" title="Recorrido sugerido por el módulo">
          <div className="space-y-4">
            <p>La lógica más clara hoy es esta: primero entra la mercadería, después se prepara la salida, luego Logística la mueve y finalmente Movimientos conserva la lectura comercial y operativa.</p>
            <div className="flex flex-wrap gap-2">
              <QuickActionButton label="Planificación" onClick={() => onGoToPanel?.('planner')} />
              <QuickActionButton label="Productos" onClick={() => onGoToPanel?.('products')} />
              <QuickActionButton label="Compras / pendientes" tone="cyan" onClick={() => onGoToPanel?.('purchases')} />
              <QuickActionButton label="Pedidos de salida" tone="emerald" onClick={() => onGoToPanel?.('outbound-orders')} />
              <QuickActionButton label="Movimientos" tone="violet" onClick={() => onGoToPanel?.('movements')} />
            </div>
            <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-stone-400">Criterio</p>
              <p className="mt-2">Control de Stock ya no es solo conteo. Hoy conecta compras, pedidos de salida, traza y control físico dentro de una misma experiencia.</p>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default VirtualAssistant;
